import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTheme } from './hooks/useTheme';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ThemeToggle from './components/ThemeToggle';
import SupportChatWidget from './components/SupportChatWidget';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import DashboardLayout from './components/DashboardLayout';

// Lazy load admin pages for better performance
            const AdminBookingsPage = lazy(() => import('./pages/admin/AdminBookingsPage'));
            const AdminSupportWorkersPage = lazy(() => import('./pages/admin/AdminSupportWorkersPage'));
            const AdminVehiclesPage = lazy(() => import('./pages/admin/AdminVehiclesPage'));
            const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
            const AdminApplicationsPage = lazy(() => import('./pages/admin/AdminApplicationsPage'));
            const AdminNotificationsPage = lazy(() => import('./pages/admin/AdminNotificationsPage'));

// Loading component for lazy-loaded routes
const AdminPageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

// Admin route wrapper
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Add admin authentication logic here
  return <>{children}</>;
};

function App() {
  const { theme } = useTheme();
  const accessibleTransportUkSeo = {
    title: 'Wheelchair Accessible Transport UK | Book Wheelchair-Friendly Rides | AbleGo',
    description:
      'Book safe, reliable accessible transport across the UK. Wheelchair-friendly vehicles with trained support.',
    canonicalPath: '/accessible-transport-uk'
  };

  useEffect(() => {
    // Set initial theme to prevent flash
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  return (
    <ErrorBoundary>
      <Helmet>
        <html lang="en" />
        <meta name="theme-color" content={theme === 'dark' ? '#0f172a' : '#ffffff'} />
      </Helmet>
      <Router>
        <div className="min-h-screen bg-white dark:bg-dark-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
          <Navbar />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/accessible-transport-uk" element={<ServicesPage seo={accessibleTransportUkSeo} />} />
            <Route path="/booking" element={<BookingPage />} />
            <Route path="/book" element={<Navigate to="/booking" replace />} />
            <Route path="/booking-status" element={<BookingStatusPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/safety" element={<SafetyPage />} />
            <Route path="/accessible-transport-uk" element={<AccessibleTransportUkPage />} />
            <Route path="/wheelchair-accessible-taxi" element={<WheelchairAccessibleTaxiPage />} />
            <Route path="/disabled-transport-services" element={<DisabledTransportServicesPage />} />
            <Route path="/auth/confirm" element={<EmailConfirmationPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/email-confirmation" element={<EmailConfirmationPage />} />
            <Route path="/admin-setup" element={<AdminSetupGuide />} />
            <Route path="/admin-login" element={<AdminLoginPage />} />
            
            {/* Lazy-loaded admin routes */}
            <Route path="/admin/bookings" element={
              <AdminRoute>
                <Suspense fallback={<AdminPageLoader />}>
                  <AdminBookingsPage />
                </Suspense>
              </AdminRoute>
            } />
            <Route path="/admin/support-workers" element={
              <AdminRoute>
                <Suspense fallback={<AdminPageLoader />}>
                  <AdminSupportWorkersPage />
                </Suspense>
              </AdminRoute>
            } />
            <Route path="/admin/vehicles" element={
              <AdminRoute>
                <Suspense fallback={<AdminPageLoader />}>
                  <AdminVehiclesPage />
                </Suspense>
              </AdminRoute>
            } />
            <Route path="/admin/users" element={
              <AdminRoute>
                <Suspense fallback={<AdminPageLoader />}>
                  <AdminUsersPage />
                </Suspense>
              </AdminRoute>
            } />
                                    <Route path="/admin/applications" element={
                          <AdminRoute>
                            <Suspense fallback={<AdminPageLoader />}>
                              <AdminApplicationsPage />
                            </Suspense>
                          </AdminRoute>
                        } />
                        <Route path="/admin/notifications" element={
                          <AdminRoute>
                            <Suspense fallback={<AdminPageLoader />}>
                              <AdminNotificationsPage />
                            </Suspense>
                          </AdminRoute>
                        } />
            
            {/* Dashboard routes */}
            <Route path="/dashboard" element={
              <DashboardLayout>
                <DashboardPage />
              </DashboardLayout>
            } />
            <Route path="/dashboard/admin" element={
              <DashboardLayout>
                <AdminDashboard />
              </DashboardLayout>
            } />
            <Route path="/dashboard/driver" element={
              <DashboardLayout>
                <DriverDashboard />
              </DashboardLayout>
            } />
            <Route path="/dashboard/support" element={
              <DashboardLayout>
                <SupportWorkerDashboard />
              </DashboardLayout>
            } />
            <Route path="/dashboard/rider" element={
              <DashboardLayout>
                <RiderDashboard />
              </DashboardLayout>
            } />
            
            {/* Registration routes */}
            <Route path="/register-driver" element={<RegisterDriverPage />} />
            <Route path="/register-support-worker" element={<RegisterSupportWorkerPage />} />
            <Route path="/join" element={<JoinPage />} />
            
            {/* Authentication routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            
            {/* Legal pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            {/* <Route path="/cookies" element={<CookiesPage />} /> */}
            {/* <Route path="/accessibility" element={<AccessibilityPage />} /> */}
            
            {/* Error pages */}
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="/500" element={<ServerErrorPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Footer />
          <SupportChatWidget />
          <ThemeToggle />
        </div>
      </Router>
    </ErrorBoundary>
  );
}

// Import all pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ServicesPage from './pages/ServicesPage';
import BookingPage from './pages/BookingPage';
import BookingStatusPage from './pages/BookingStatusPage';
import ContactPage from './pages/ContactPage';
import FAQPage from './pages/FAQPage';
import SafetyPage from './pages/SafetyPage';
import AccessibleTransportUkPage from './pages/AccessibleTransportUkPage';
import WheelchairAccessibleTaxiPage from './pages/WheelchairAccessibleTaxiPage';
import DisabledTransportServicesPage from './pages/DisabledTransportServicesPage';
import EmailConfirmationPage from './pages/EmailConfirmationPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminSetupGuide from './components/AdminSetupGuide';
import AdminLoginPage from './pages/AdminLoginPage';
import DashboardPage from './pages/DashboardPage';
import AdminDashboard from './pages/dashboard/AdminDashboard';
import DriverDashboard from './pages/dashboard/DriverDashboard';
import SupportWorkerDashboard from './pages/dashboard/SupportWorkerDashboard';
import RiderDashboard from './pages/dashboard/RiderDashboard';
import RegisterDriverPage from './pages/DriverRegistrationPage';
import RegisterSupportWorkerPage from './pages/SupportWorkerRegistrationPage';
import JoinPage from './pages/JoinPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ResetPasswordPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';
// import CookiesPage from './pages/CookiesPage';
// import AccessibilityPage from './pages/AccessibilityPage';
import NotFoundPage from './pages/NotFoundPage';
import ServerErrorPage from './pages/ServerErrorPage';

export default App;